import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

interface SurveyState {
  firstDeliveryCompletedAt: string | null;
  surveyPrompted: boolean;
  surveyDismissedCount: number;
}

// Minimum delay after first delivery before showing survey (2 hours in ms)
const MIN_DELAY_MS = 2 * 60 * 60 * 1000;

export const useSurveyPrompt = (userId: string | undefined) => {
  const [showSurvey, setShowSurvey] = useState(false);
  const [loading, setLoading] = useState(true);
  const [surveyState, setSurveyState] = useState<SurveyState | null>(null);

  // Check if user is eligible for survey prompt
  const checkEligibility = useCallback(async () => {
    if (!userId) {
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await (supabase as any)
        .from("profiles")
        .select("first_delivery_completed_at, survey_prompted, survey_dismissed_count")
        .eq("id", userId)
        .maybeSingle();

      if (error) throw error;

      if (!data) {
        setLoading(false);
        return;
      }

      setSurveyState({
        firstDeliveryCompletedAt: data.first_delivery_completed_at,
        surveyPrompted: data.survey_prompted ?? false,
        surveyDismissedCount: data.survey_dismissed_count ?? 0,
      });

      // Determine if we should show survey
      const shouldShow = determineShouldShow(
        data.first_delivery_completed_at,
        data.survey_prompted ?? false,
        data.survey_dismissed_count ?? 0
      );

      setShowSurvey(shouldShow);
    } catch (err) {
      if (import.meta.env.DEV) {
        console.error("Error checking survey eligibility:", err);
      }
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    checkEligibility();
  }, [checkEligibility]);

  // Logic to determine if survey should be shown
  const determineShouldShow = (
    firstDeliveryAt: string | null,
    prompted: boolean,
    dismissedCount: number
  ): boolean => {
    // No first delivery yet
    if (!firstDeliveryAt) return false;

    // Already completed survey
    if (prompted) return false;

    // Dismissed twice already (max retries reached)
    if (dismissedCount >= 2) return false;

    // Check if minimum delay has passed (2 hours)
    const deliveryTime = new Date(firstDeliveryAt).getTime();
    const now = Date.now();
    const elapsed = now - deliveryTime;

    if (elapsed < MIN_DELAY_MS) return false;

    return true;
  };

  // Mark survey as completed (user clicked to take it)
  const markSurveyCompleted = useCallback(async () => {
    if (!userId) return;

    try {
      await (supabase as any)
        .from("profiles")
        .update({
          survey_prompted: true,
          updated_at: new Date().toISOString(),
        })
        .eq("id", userId);

      setShowSurvey(false);
      setSurveyState((prev) =>
        prev ? { ...prev, surveyPrompted: true } : null
      );
    } catch (err) {
      if (import.meta.env.DEV) {
        console.error("Error marking survey completed:", err);
      }
    }
  }, [userId]);

  // Handle "Maybe later" - increment dismissed count
  const dismissSurvey = useCallback(async () => {
    if (!userId || !surveyState) return;

    const newDismissedCount = surveyState.surveyDismissedCount + 1;

    try {
      await (supabase as any)
        .from("profiles")
        .update({
          survey_dismissed_count: newDismissedCount,
          // If dismissed twice, mark as permanently prompted to avoid future checks
          ...(newDismissedCount >= 2 && { survey_prompted: true }),
          updated_at: new Date().toISOString(),
        })
        .eq("id", userId);

      setShowSurvey(false);
      setSurveyState((prev) =>
        prev
          ? {
              ...prev,
              surveyDismissedCount: newDismissedCount,
              surveyPrompted: newDismissedCount >= 2,
            }
          : null
      );
    } catch (err) {
      if (import.meta.env.DEV) {
        console.error("Error dismissing survey:", err);
      }
    }
  }, [userId, surveyState]);

  return {
    showSurvey,
    loading,
    markSurveyCompleted,
    dismissSurvey,
    closeSurveyModal: () => setShowSurvey(false),
  };
};
